import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setS({ ...s, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Einstellungen gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <p className="text-[#9A86B4]">Laden…</p>;

  return (
    <div className="fade-in max-w-3xl">
      <h1 className="font-serif text-4xl font-light mb-8">Einstellungen</h1>
      <Tabs defaultValue="general">
        <TabsList className="rounded-md bg-white border border-[#E7D8C6] mb-6 flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="tab-general" className="rounded-md">Allgemein</TabsTrigger>
          <TabsTrigger value="pages" data-testid="tab-pages" className="rounded-md">Seiten</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email" className="rounded-md">E-Mail</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-md">Kategorien</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="space-y-6 bg-white border border-[#E7D8C6] p-5 sm:p-8">
            <Section title="Startseite (Hero)">
              <F label="Titel"><Input value={s.hero_title} onChange={set("hero_title")} className="rounded-md h-12" /></F>
              <F label="Untertitel"><Input value={s.hero_subtitle} onChange={set("hero_subtitle")} className="rounded-md h-12" /></F>
              <F label="Einführungstext"><Textarea rows={3} value={s.hero_text} onChange={set("hero_text")} className="rounded-md" /></F>
              <ImageUpload label="Hero-Bild" value={s.hero_image} onChange={set("hero_image")} />
            </Section>
            <Section title="Künstlerin (Startseite)">
              <F label="Kurzvorstellung"><Textarea rows={3} value={s.about_short} onChange={set("about_short")} className="rounded-md" /></F>
              <ImageUpload label="Portrait" value={s.about_portrait} onChange={set("about_portrait")} />
            </Section>
            <Section title="Allgemein">
              <F label="Slogan"><Input value={s.tagline} onChange={set("tagline")} className="rounded-md h-12" /></F>
              <F label="Kontakt E-Mail"><Input value={s.contact_email} onChange={set("contact_email")} className="rounded-md h-12" /></F>
              <F label="Kontakt Telefon"><Input value={s.contact_phone} onChange={set("contact_phone")} className="rounded-md h-12" /></F>
            </Section>
            <Section title="Social Media (nur angezeigt, wenn ausgefüllt)">
              <F label="Instagram URL"><Input value={s.instagram} onChange={set("instagram")} className="rounded-md h-12" placeholder="https://instagram.com/…" /></F>
              <F label="Facebook URL"><Input value={s.facebook} onChange={set("facebook")} className="rounded-md h-12" /></F>
              <F label="LinkedIn URL"><Input value={s.linkedin} onChange={set("linkedin")} className="rounded-md h-12" /></F>
            </Section>
            <Button onClick={save} disabled={saving} data-testid="save-settings" className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md">Speichern</Button>
          </div>
        </TabsContent>

        <TabsContent value="pages">
          <div className="space-y-6">
            <PageEditor pageKey="ueber-mich" title="Über mich" />
            <PageEditor pageKey="impressum" title="Impressum" />
            <PageEditor pageKey="datenschutz" title="Datenschutz" />
          </div>
        </TabsContent>

        <TabsContent value="categories">
          <div className="space-y-6">
            <CategoryManager endpoint="artwork-categories" title="Kunst-Kategorien" testPrefix="art-cat" />
            <CategoryManager endpoint="poem-categories" title="Gedicht-Kategorien" testPrefix="poem-cat" />
          </div>
        </TabsContent>

        <TabsContent value="email">
          <EmailSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="font-serif text-xl border-b border-[#F5EBDB] pb-2">{title}</h3>
      {children}
    </div>
  );
}
function F({ label, children }) {
  return <div><Label className="mb-1.5 block">{label}</Label>{children}</div>;
}

function PageEditor({ pageKey, title }) {
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/pages/${pageKey}`).then(({ data }) => setPage(data)).catch(() => {}); }, [pageKey]);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/pages/${pageKey}`, { title: page.title, content: page.content }); toast.success(`${title} gespeichert`); }
    catch { toast.error("Fehler"); } finally { setSaving(false); }
  };
  if (!page) return null;
  return (
    <div className="bg-white border border-[#E7D8C6] p-5 sm:p-8 space-y-4">
      <h3 className="font-serif text-2xl">{title}</h3>
      <F label="Seitentitel"><Input value={page.title} onChange={(e) => setPage({ ...page, title: e.target.value })} className="rounded-md h-12" /></F>
      <F label="Inhalt"><Textarea rows={8} value={page.content} onChange={(e) => setPage({ ...page, content: e.target.value })} className="rounded-md whitespace-pre-wrap" /></F>
      <Button onClick={save} disabled={saving} data-testid={`save-page-${pageKey}`} className="bg-[#F4C542] text-[#32104F] rounded-md">Speichern</Button>
    </div>
  );
}

function CategoryManager({ endpoint, title, testPrefix }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get(`/${endpoint}`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(`/admin/${endpoint}`, { name }); setName(""); load(); toast.success("Kategorie hinzugefügt"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const del = async (id) => { await api.delete(`/admin/${endpoint}/${id}`).catch(() => {}); load(); };
  return (
    <div className="bg-white border border-[#E7D8C6] p-5 sm:p-8">
      <h3 className="font-serif text-2xl mb-4">{title}</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        {items.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-2 border border-[#E6D7EF] px-3 py-1.5 text-sm">
            {c.name}
            <button onClick={() => del(c.id)} data-testid={`del-${testPrefix}-${c.slug}`} aria-label="Entfernen" className="text-[#9A86B4] hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Neue Kategorie…" data-testid={`input-${testPrefix}`} className="rounded-md h-11" />
        <Button onClick={add} data-testid={`add-${testPrefix}`} className="bg-[#F4C542] text-[#32104F] rounded-md"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}


function StatusRow({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`email-status-${label.toLowerCase().replace(/[^a-z]/g, "")}`}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
      <span className={ok ? "text-[#3A1E55]" : "text-red-600"}>{label}: {ok ? "OK" : "Fehler"}</span>
    </div>
  );
}

function EmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [to, setTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/email-config").then(({ data }) => { setCfg(data); setTo(data.admin_notify_email || ""); }).catch(() => {});
  }, []);

  const runTest = async () => {
    if (!to.trim()) { toast.error("Bitte eine Empfängeradresse eingeben."); return; }
    setTesting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email-test", { to: to.trim() });
      setResult(data);
      if (data.test_email) toast.success("Test-E-Mail erfolgreich versendet.");
      else toast.error("E-Mail-Test fehlgeschlagen.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test fehlgeschlagen.");
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) return <p className="text-[#9A86B4]">Laden…</p>;

  return (
    <div className="space-y-6 bg-white border border-[#E7D8C6] p-5 sm:p-8" data-testid="email-settings">
      <div>
        <h3 className="font-serif text-2xl mb-1">E-Mail-Versand (SMTP)</h3>
        <p className="text-sm text-[#6E5A82]">Serverseitig konfiguriert über sichere Umgebungsvariablen. Passwörter werden nie angezeigt.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <Field label="Status" value={cfg.configured ? "Konfiguriert ✓" : "Nicht konfiguriert (SMTP_PASSWORD fehlt)"} />
        <Field label="Server" value={`${cfg.host}:${cfg.port}`} />
        <Field label="TLS" value={cfg.starttls ? "STARTTLS" : "aus"} />
        <Field label="Absender" value={`${cfg.from_name} <${cfg.from_email}>`} />
        <Field label="Benutzer" value={cfg.user} />
        <Field label="Antwort an" value={cfg.reply_to || "–"} />
        <Field label="Admin-Benachrichtigung" value={cfg.admin_notify_email || "–"} />
      </div>

      {!cfg.configured && (
        <div className="rounded-md bg-[#FFF4D6] border border-[#F4C542]/50 p-4 text-sm text-[#6E4A00]" data-testid="email-not-configured">
          Der E-Mail-Versand ist noch nicht aktiv. Das Google App-Passwort muss serverseitig in <code>SMTP_PASSWORD</code> hinterlegt werden.
        </div>
      )}

      <div className="border-t border-[#F5EBDB] pt-6">
        <Label className="mb-1.5 block">E-Mail-Konfiguration testen</Label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="empfaenger@example.com" data-testid="email-test-to" className="rounded-md h-11 bg-white" />
          <Button onClick={runTest} disabled={testing} data-testid="email-test-submit" className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md h-11 px-6 whitespace-nowrap">
            {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Teste…</> : "Test-E-Mail senden"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="rounded-md border border-[#E7D8C6] p-4 space-y-2" data-testid="email-test-result">
          <StatusRow ok={result.smtp_connection} label="SMTP-Verbindung" />
          <StatusRow ok={result.tls} label="TLS" />
          <StatusRow ok={result.auth} label="Authentifizierung" />
          <StatusRow ok={result.test_email} label="Test-E-Mail" />
          {result.error && <p className="text-sm text-red-600 mt-2" data-testid="email-test-error">Fehler: {result.error}</p>}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-[#F5EBDB]">
      <span className="text-[#9A86B4]">{label}</span>
      <span className="text-[#3A1E55] text-right break-all">{value}</span>
    </div>
  );
}
